import { useMemo, useState } from "react";
import { Loader2, Pencil, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { RichText } from "@/components/ui/RichText";
import { WysiwygEditor } from "@/components/ui/WysiwygEditor";
import {
  usePulseEntries,
  useAddPulseEntry,
  useUpdatePulseEntry,
  useDeletePulseEntry,
  type PulseEntry,
} from "@/hooks/usePulse";
import { useRoster } from "@/hooks/useRoster";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";

// Status Pulse journal — the pulse as an ongoing dated dialog (Brett,
// 2026-09-03) instead of one overwritten blob. Every post auto-stamps
// date + author; entries group under day headers; @mentions pick from
// the roster and render highlighted. The newest entry doubles as
// projects.status_update server-side, so every "latest pulse" surface
// keeps working.

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return "Yesterday";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function PulseJournal({ projectId }: { projectId: string }) {
  const { user } = useAuth();
  const { data: entries, isLoading } = usePulseEntries(projectId);
  const { data: roster } = useRoster();
  const addEntry = useAddPulseEntry(projectId);

  const mentionNames = useMemo(
    () => (roster ?? []).map((m: { name: string }) => m.name).filter(Boolean),
    [roster],
  );

  // Group newest-first entries under their local-day headers.
  const days = useMemo(() => {
    const out: Array<{ key: string; label: string; items: PulseEntry[] }> = [];
    for (const e of entries ?? []) {
      const key = dayKey(e.created_at);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(e);
      else out.push({ key, label: dayLabel(e.created_at), items: [e] });
    }
    return out;
  }, [entries]);

  return (
    <div className="space-y-4">
      <Composer
        placeholder="What's happening? Type @ to tag someone…"
        mentionNames={mentionNames}
        pending={addEntry.isPending}
        onSubmit={async (body) => {
          await addEntry.mutateAsync(body);
        }}
      />

      {isLoading ? (
        <div className="p-6 text-center text-sm text-ink-subtle">Loading…</div>
      ) : days.length === 0 ? (
        <p className="px-1 text-sm italic text-ink-subtle">
          No entries yet. Post the first update — the date and your name
          are stamped automatically.
        </p>
      ) : (
        days.map((day) => (
          <section key={day.key}>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                {day.label}
              </span>
              <div className="h-px flex-1 bg-raised" />
            </div>
            <div className="space-y-2">
              {day.items.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  projectId={projectId}
                  mentionNames={mentionNames}
                  canManage={
                    user != null &&
                    (user.role === "admin" ||
                      e.author_email.toLowerCase() === user.email.toLowerCase())
                  }
                />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function EntryCard({
  entry,
  projectId,
  mentionNames,
  canManage,
}: {
  entry: PulseEntry;
  projectId: string;
  mentionNames: string[];
  canManage: boolean;
}) {
  const updateEntry = useUpdatePulseEntry(projectId);
  const deleteEntry = useDeletePulseEntry(projectId);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const author =
    entry.author_name || (entry.author_email ? entry.author_email.split("@")[0] : "Earlier note");

  return (
    <div className="group rounded-lg border border-line bg-surface p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-semibold text-ink-2">{author}</span>
        <span className="text-[11px] tabular-nums text-ink-subtle">
          {timeLabel(entry.created_at)}
        </span>
        {entry.edited_at && (
          <span className="text-[10px] italic text-ink-faint">edited</span>
        )}
        {canManage && !editing && (
          <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={() => setEditing(true)}
              className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-2"
              title="Edit entry"
            >
              <Pencil className="h-3 w-3" />
            </button>
            {confirmDelete ? (
              <button
                onClick={async () => {
                  try {
                    await deleteEntry.mutateAsync(entry.id);
                  } catch {
                    toast.error("Failed to delete entry.");
                  }
                }}
                className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-rose-700"
              >
                Confirm
              </button>
            ) : (
              <button
                onClick={() => {
                  setConfirmDelete(true);
                  setTimeout(() => setConfirmDelete(false), 3000);
                }}
                className="rounded p-1 text-ink-subtle hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400"
                title="Delete entry"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </span>
        )}
      </div>
      {editing ? (
        <Composer
          initial={entry.body}
          mentionNames={mentionNames}
          pending={updateEntry.isPending}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (body) => {
            await updateEntry.mutateAsync({ id: entry.id, body });
            setEditing(false);
          }}
        />
      ) : (
        <RichText text={entry.body} mentionNames={mentionNames} />
      )}
    </div>
  );
}

// Shared composer for new posts and edits. Word-style WYSIWYG (Brett,
// 2026-09-04 — "most users do not understand markdown"): the toolbar
// formats visually, "@" opens the roster picker, and the stored value
// stays markdown under the hood so rendering and search are unchanged.
function Composer({
  initial = "",
  placeholder,
  mentionNames,
  pending,
  submitLabel = "Post",
  onSubmit,
  onCancel,
}: {
  initial?: string;
  placeholder?: string;
  mentionNames: string[];
  pending: boolean;
  submitLabel?: string;
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
}) {
  const [value, setValue] = useState(initial);

  const submit = async () => {
    const body = value.trim();
    if (!body) return;
    try {
      await onSubmit(body);
      if (!onCancel) setValue(""); // new-post composer clears; edit closes
    } catch {
      toast.error("Failed to save the entry.");
    }
  };

  return (
    <div>
      <WysiwygEditor
        value={value}
        onChange={setValue}
        placeholder={placeholder}
        autoFocus={!!onCancel}
        minHeightClass="min-h-[4.5rem]"
        mentionNames={mentionNames}
        onModEnter={() => void submit()}
      />
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[10px] text-ink-subtle">
          @ to tag · ⌘↵ to {submitLabel.toLowerCase()}
        </span>
        <div className="flex gap-1.5">
          {onCancel && (
            <button
              onClick={onCancel}
              className="rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => void submit()}
            disabled={pending || !value.trim()}
            className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-50"
          >
            {pending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
